import { useEffect,useState } from "react";
import ImageUploading from "react-images-uploading";
import { v4 as uuidv4 } from "uuid";

import PopupUpdateArticleCover from "@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateArticleCover";
import UserService from "@/services/user.service";
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';

import "./styles/UploadCover.css";

export default function UploadCover({ 
  formState,
  setFormState,
  element,
  setFormError,
  onImageUploadRef,
  onCropPopupToggle,
  croppedContainerClass='' }) {
  const [openCropPopup, setOpenCropPopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedUrl, setCroppedUrl] = useState(null);

  // inform parent whenever our crop popup opens or closes
  useEffect(() => {
    if (onCropPopupToggle) onCropPopupToggle(openCropPopup);
  }, [openCropPopup, onCropPopupToggle]);

  // Upload the cropped file only once using the helper from your working profile picture flow
  async function uploadCroppedCover(croppedImage) {
    const temporalImageId = uuidv4();

    setFormError(null);
    setFormState((prevValue) => ({
      ...prevValue,
      post_cover: {
        loading: true,
        progress: 0,
        temporalImageId,
      },
    }));

    const { response, data } = await UserService.uploadImage(croppedImage, (progress) => {
      setFormState((prevValue) => ({
        ...prevValue,
        post_cover: { ...prevValue.post_cover, progress },
      }));
    });

    if (response.ok && data && data.image_id) {
      setFormState((prevValue) => ({
        ...prevValue,
        post_cover: { image_id: data.image_id },
      }));
      const fileUrl = URL.createObjectURL(croppedImage);
      setCroppedUrl(fileUrl);
    } else {
      console.error("Error uploading cropped cover:", response);
      setFormState((prevValue) => ({ ...prevValue, post_cover: null }));
      alert("Something went wrong. Please try again.");
    }
  }

  // Called when cropping is complete – show a preview+spinner, then kick off upload
  const handleCropComplete = async (croppedImage) => {
    try {
      setOpenCropPopup(false);
      // 1) Immediately create a blob‐URL so our UserProfileImage mounts
      const previewUrl = URL.createObjectURL(croppedImage);
      setCroppedUrl(previewUrl);

      // 2) Then upload (this sets formState.post_cover.loading = true)
      await uploadCroppedCover(croppedImage);
    } catch (error) {
      console.error("Error in cropping/uploading cover:", error);
    }
  };

  return (
    <>
      <ImageUploading
        maxNumber={1}
        onChange={(imagesList) => {
          setSelectedImage(imagesList[0].file);
          setOpenCropPopup(true);
        }}
        onError={(errors) => {
          console.error("Image upload errors:", errors);
          if (errors.maxNumber)
            setFormError({ element, error_code: "ONLY_ONE_FILE" });
          else if (errors.acceptType)
            setFormError({ element, error_code: "INVALID_FILE_FORMAT" });
          else if (errors.maxFileSize)
            setFormError({ element, error_code: "FILE_EXCEEDS_SIZE" });
        }}
        maxFileSize={15000000}
      >
        {({ onImageUpload }) => {
          if (onImageUploadRef) {
            onImageUploadRef.current = onImageUpload;
          }
          return null;
        }}
      </ImageUploading>

      {openCropPopup && (
        <PopupUpdateArticleCover
          open={openCropPopup}
          image={selectedImage}
          exitCrop={() => setOpenCropPopup(false)}
          setCroppedFile={handleCropComplete}
          setCroppedUrl={setCroppedUrl}
        />
      )}

      {croppedUrl && (
        <div className={`post-cover__wrapper ${croppedContainerClass}`}>
          {/* 3) use UserProfileImage for the same loader+preview behavior */}
          <UserProfileImage
            className="post-cover__image"
            image={croppedUrl}                         // your blob URL
            loading={formState.post_cover?.loading}    // show spinner while uploading
            typeOfProfile="gallery"                     // choose an icon style if no image
            width={640}
            height={360}
            style={{ objectFit: 'cover' }}
          />
        </div>
      )}
    </>
  );
}
